// What the Stores list shows, kept in the URL query (WEB_PLAN.md §5.5): the MapFilter as `filterToQuery` writes it
// (so a Dashboard tile can link straight to "/stores?followUp=1"), plus the sort and the Archived toggle. The search
// text stays out of the URL on purpose (the plan keeps only ids there, so a shared link never carries what someone
// typed); it lives in sessionStorage so Back from a store finds it again.

import {
  RECORD_LIST_SORTS,
  RECORD_LIST_SORT_TITLES,
  filterFromQuery,
  filterToQuery,
  type MapFilter,
  type RecordListSort,
} from '@/domain/records';
import { isColumnId, type ColumnSort } from './columns';

/** The iOS sorts the console offers: "Distance from me" needs the phone's location, so it is left out (as iOS hides it without one). */
export type ListSort = Exclude<RecordListSort, 'distance'>;
export const LIST_SORTS: readonly ListSort[] = RECORD_LIST_SORTS.filter((s): s is ListSort => s !== 'distance');
/** `MapSession.listSort`'s default. */
export const DEFAULT_LIST_SORT: ListSort = 'daysSinceVisitLeast';

export interface StoresListState {
  filter: MapFilter;
  /** The iOS sort option, used while no column header sort is set. */
  sort: ListSort;
  /** Header sorts (first = primary); non-empty overrides `sort`. */
  columnSort: readonly ColumnSort[];
  /** Include archived stores. */
  archived: boolean;
}

const Q = { sort: 'sort', col: 'col', archived: 'archived' } as const;
const MAX_COLUMN_SORTS = 3;

function isListSort(v: string | null): v is ListSort {
  return v !== null && (LIST_SORTS as readonly string[]).includes(v);
}

/** Reads the list state; unknown or malformed values fall back to the defaults. */
export function listStateFromQuery(p: URLSearchParams): StoresListState {
  const sort = p.get(Q.sort);
  const columnSort: ColumnSort[] = [];
  for (const raw of p.getAll(Q.col)) {
    const at = raw.lastIndexOf(':');
    if (at <= 0) continue;
    const id = raw.slice(0, at);
    const dir = raw.slice(at + 1);
    const key = id.startsWith('cf:') ? id.toLowerCase() : id;
    if (!isColumnId(key) || (dir !== 'asc' && dir !== 'desc')) continue;
    if (columnSort.some((c) => c.id === key)) continue;
    columnSort.push({ id: key, desc: dir === 'desc' });
    if (columnSort.length >= MAX_COLUMN_SORTS) break;
  }
  return {
    filter: filterFromQuery(p),
    sort: isListSort(sort) ? sort : DEFAULT_LIST_SORT,
    columnSort,
    archived: p.get(Q.archived) === '1',
  };
}

/** The query for a list state; defaults are left out so a plain list is just "/stores". */
export function listStateToQuery(s: StoresListState): URLSearchParams {
  const p = filterToQuery(s.filter);
  if (s.columnSort.length > 0) {
    for (const c of s.columnSort.slice(0, MAX_COLUMN_SORTS)) p.append(Q.col, `${c.id}:${c.desc ? 'desc' : 'asc'}`);
  } else if (s.sort !== DEFAULT_LIST_SORT) {
    p.set(Q.sort, s.sort);
  }
  if (s.archived) p.set(Q.archived, '1');
  return p;
}

/** Same query string? (avoids pushing an identical URL). */
export function sameQuery(a: URLSearchParams, b: URLSearchParams): boolean {
  return a.toString() === b.toString();
}

/**
 * Header sorts after a click, at most three levels. Shift+click appends a level; past the cap the oldest goes (what
 * TanStack's `maxMultiSortColCount` does), so the column just clicked always takes effect. Trimming from the end
 * instead, as the URL writer would, silently ignored the fourth click.
 */
export function capColumnSorts(next: readonly ColumnSort[]): ColumnSort[] {
  return next.length > MAX_COLUMN_SORTS ? next.slice(next.length - MAX_COLUMN_SORTS) : [...next];
}

/**
 * Where the detail page's "Stores" link goes: the list view the store was opened from (its query, carried in the
 * router state), else the plain list. Only a query string is accepted, so the link always stays on /stores.
 */
export function storesBackHref(state: unknown): string {
  const listSearch = typeof state === 'object' && state !== null ? (state as { listSearch?: unknown }).listSearch : undefined;
  return typeof listSearch === 'string' && listSearch.startsWith('?') && listSearch.length > 1 ? `/stores${listSearch}` : '/stores';
}

/**
 * "Days Since Last Visit: Least first" (iOS "Sorted by …" line), or for header sorts "Value, high to low, then
 * Name, A to Z".
 */
export function sortDescription(s: Pick<StoresListState, 'sort' | 'columnSort'>, titleOf: (columnId: string) => string | undefined): string {
  if (s.columnSort.length === 0) return RECORD_LIST_SORT_TITLES[s.sort];
  return s.columnSort
    .map((c) => `${titleOf(c.id) ?? 'Column'}, ${c.desc ? 'descending' : 'ascending'}`)
    .join(', then ');
}

// MARK: - Search text (per tab)

export const SEARCH_KEY = 'gt.stores.search';
const MAX_SEARCH = 200;

export function loadSearch(storage: Pick<Storage, 'getItem'> | null | undefined): string {
  try {
    const v = storage?.getItem(SEARCH_KEY);
    return typeof v === 'string' ? v.slice(0, MAX_SEARCH) : '';
  } catch {
    return '';
  }
}

export function saveSearch(storage: Pick<Storage, 'setItem' | 'removeItem'> | null | undefined, text: string): void {
  try {
    if (!storage) return;
    if (text === '') storage.removeItem(SEARCH_KEY);
    else storage.setItem(SEARCH_KEY, text.slice(0, MAX_SEARCH));
  } catch {
    // Storage blocked: the search still works, it just will not survive a reload.
  }
}
